/* Gasto: se teclea en yenes y la moneda cae en la caja de ofrendas (賽銭箱). */
import { useEffect, useRef, useState } from 'react';
import { apuntarGasto, cambiarGasto, cambioDe, enEuros, enYenes, gastos, gastosConId, ponerCambio, useApuntes, type Gasto } from '../datos/apuntes.ts';
import type { Viaje } from '../datos/tipos.ts';
import { reducido } from '../escena/motor.ts';
import { Hoja } from '../componentes/Hoja.tsx';
import { euros, miles, num2 } from '../texto.ts';
import { ahora } from '../tiempo.ts';

const CATEGORIAS: [string, string][] = [['Comida/Restaurantes', 'Comida'], ['Transporte Local', 'Transporte'], ['Supermercado', 'Súper'], ['Actividades/Entradas', 'Entradas'], ['Compras y otros', 'Compras'], ['Día JDM', 'Día JDM']];
/** Para quién es cada gasto: una persona, la familia… Los gastos son de cada uno (no se reparten entre los tres). */
const PARA = ['Yo', 'Familia', 'Amigos', 'Pareja'];
const CLAVE_PARA = 'tabiji.gasto.para';
const leerPara = () => { try { return localStorage.getItem(CLAVE_PARA) ?? 'Yo'; } catch { return 'Yo'; } };
const TECLAS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0', '⌫'];

function Caja() {
  return (
    <svg viewBox="0 0 170 110">
      <g id="caja">
        <path d="M22,40 L148,40 L138,106 L32,106 Z" fill="#8a5a2b" /><path d="M22,40 L148,40 L146,52 L24,52 Z" fill="#6f4520" /><rect x="14" y="28" width="142" height="14" rx="3" fill="#5e3a1a" />
        {Array.from({ length: 10 }, (_, k) => <rect key={k} x={22 + k * 13} y="30" width="7" height="10" rx="1.5" fill="#24160a" />)}
        <rect id="ranura" x="70" y="30" width="30" height="4" fill="none" />
        <text x="85" y="86" textAnchor="middle" fontSize="24" fontWeight="800" fill="#f3dca8" fontFamily="Shippori Mincho B1, serif">奉納</text>
        <rect x="22" y="94" width="10" height="12" fill="#d8b04a" /><rect x="138" y="94" width="10" height="12" fill="#d8b04a" />
      </g>
    </svg>
  );
}

export function HojaGasto({ v, n, onCerrada }: { v: Viaje; n: number; onCerrada: () => void }) {
  useApuntes();
  const yenPorEuro = cambioDe(v.viaje.yenPorEuro);
  const [monto, setMonto] = useState('');
  const [moneda, setMoneda] = useState<'JPY' | 'EUR'>('JPY');
  const [editando, setEditando] = useState(false);
  const [cat, setCat] = useState(CATEGORIAS[0][0]);
  const [concepto, setConcepto] = useState('');
  const [para, setPara] = useState(leerPara);
  const [otro, setOtro] = useState(() => (PARA.includes(leerPara()) ? '' : leerPara()));
  const [esRegalo, setEsRegalo] = useState(false);
  const [regalo, setRegalo] = useState('');
  const [editado, setEditado] = useState<(Gasto & { id: string }) | null>(null);
  const salida = useRef<HTMLOutputElement>(null), total = useRef<HTMLElement>(null), totalEur = useRef<HTMLSpanElement>(null), anuncio = useRef<HTMLParagraphElement>(null);
  const todos = gastos();
  const totalDia = (dia: number, lista = todos) => lista.filter(g => g.dia === dia).reduce((s, g) => s + enYenes(g, yenPorEuro), 0);
  const [animado, setAnimado] = useState<number | null>(null);   // el total mientras sube la cuenta
  const mostrado = animado ?? totalDia(n);
  // En euros se teclean céntimos, como en un datáfono: 1 2 5 0 → 12,50 €
  const cifra = +monto || 0;
  const y = moneda === 'JPY' ? cifra : Math.round((cifra / 100) * yenPorEuro);
  const eur = moneda === 'EUR' ? cifra / 100 : cifra / yenPorEuro;

  const tecla = (k: string) => setMonto(m => k === '⌫' ? m.slice(0, -1) : m.length < 7 ? (m + k).replace(/^0+/, '') : m);
  const cambiarMoneda = (m: 'JPY' | 'EUR') => { if (m !== moneda) { setMoneda(m); setMonto(''); } };

  const paraFinal = () => (para === 'Otro' ? otro.trim() || 'Otro' : para);
  const elegirPara = (x: string) => { setPara(x); try { localStorage.setItem(CLAVE_PARA, x === 'Otro' ? otro : x); } catch { /* sin sitio */ } };
  const datosExtra = () => ({ para: paraFinal(), ...(esRegalo ? { regalo: regalo.trim() || 'Regalo' } : { regalo: undefined }) });
  const limpiar = () => { setMonto(''); setConcepto(''); setEditado(null); setEsRegalo(false); setRegalo(''); };
  const importe = () => (moneda === 'JPY' ? { yen: cifra } : { eur: cifra / 100 });

  // Tocar un gasto de la lista lo sube al teclado para corregirlo o borrarlo
  const editar = (g: Gasto & { id: string }) => {
    setEditado(g); setCat(g.cat); setConcepto(g.concepto ?? '');
    const p = g.para ?? 'Yo';
    if (PARA.includes(p)) { setPara(p); setOtro(''); } else { setPara('Otro'); setOtro(p); }
    setEsRegalo(!!g.regalo); setRegalo(g.regalo && g.regalo !== 'Regalo' ? g.regalo : '');
    if (g.yen != null) { setMoneda('JPY'); setMonto(String(g.yen)); } else { setMoneda('EUR'); setMonto(String(Math.round((g.eur ?? 0) * 100))); }
    salida.current?.closest('dialog')?.querySelector('.hoja__dentro')?.scrollTo({ top: 0, behavior: reducido() ? 'auto' : 'smooth' });
  };
  const guardarEdicion = () => {
    if (!editado || !cifra) return;
    const { id, yen: _y, eur: _e, ...resto } = editado;
    cambiarGasto(id, { ...resto, ...importe(), cat, concepto: concepto.trim(), ...datosExtra() });
    if (anuncio.current) anuncio.current.textContent = 'Gasto corregido';
    limpiar();
  };
  const borrar = () => {
    if (!editado) return;
    cambiarGasto(editado.id, null);
    if (anuncio.current) anuncio.current.textContent = 'Gasto borrado';
    limpiar();
  };

  const echar = () => {
    if (!cifra) return;
    if (editado) { guardarEdicion(); return; }
    const antes = totalDia(n), t = ahora(), hora = `${num2(t.getHours())}:${num2(t.getMinutes())}`;
    apuntarGasto({ dia: n, hora, ...importe(), cat, concepto: concepto.trim(), ...datosExtra() });
    setMonto(''); setConcepto(''); setEsRegalo(false); setRegalo('');
    const final = () => {
      const despues = antes + y, t0 = performance.now();
      const paso = (tt: number) => {
        const k = Math.min(1, (tt - t0) / 600);
        setAnimado(Math.round(antes + (despues - antes) * (1 - (1 - k) ** 3)));
        if (k < 1) requestAnimationFrame(paso); else setAnimado(null);
      };
      requestAnimationFrame(paso);
      if (anuncio.current) anuncio.current.textContent = moneda === 'JPY' ? `Apuntados ${miles(cifra)} yenes` : `Apuntados ${euros(cifra / 100, 2)}`;
    };
    if (reducido() || !salida.current) { final(); return; }
    const d = salida.current.closest('dialog')!;
    const ranura = d.querySelector('#ranura')!;
    const a = salida.current.getBoundingClientRect(), b = ranura.getBoundingClientRect();
    const x0 = a.left + a.width / 2 - 22, y0 = a.top, x1 = b.left + b.width / 2 - 22, y1 = b.top - 30;
    const m = document.createElement('div');
    m.className = 'moneda';
    d.append(m);                       // dentro de la hoja: el diálogo modal está en la capa superior
    m.animate([
      { transform: `translate(${x0}px, ${y0}px) rotate(0) scale(1)` },
      { transform: `translate(${(x0 + x1) / 2}px, ${Math.min(y0, y1) - 80}px) rotate(300deg) scale(1.15)`, offset: 0.45 },
      { transform: `translate(${x1}px, ${y1}px) rotate(620deg) scale(.9)`, offset: 0.85 },
      { transform: `translate(${x1}px, ${y1 + 26}px) rotate(640deg) scale(.7, .15)`, opacity: 0 },
    ], { duration: 950, easing: 'cubic-bezier(.3,.1,.3,1)', fill: 'forwards' }).onfinish = () => {
      m.remove();
      d.querySelector('#caja')?.animate([{ transform: 'rotate(0)' }, { transform: 'rotate(-3deg)' }, { transform: 'rotate(2.5deg)' }, { transform: 'rotate(0)' }], { duration: 380, easing: 'ease-out' });
      navigator.vibrate?.([10, 40, 10]);
      final();
    };
  };

  // Teclado físico: números, borrar e Intro
  useEffect(() => {
    const d = salida.current?.closest('dialog');
    if (!d) return;
    const f = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.matches('input:not([type=radio]), textarea')) return;      // escribiendo el cambio, no el gasto
      if (el.matches('input[type=radio]') && !/^\d$|Backspace/.test(e.key)) return;
      if (/^\d$/.test(e.key)) { tecla(e.key); e.preventDefault(); }
      if (e.key === 'Backspace') { tecla('⌫'); e.preventDefault(); }
      if (e.key === 'Enter' && cifra) { echar(); e.preventDefault(); }
    };
    d.addEventListener('keydown', f);
    return () => d.removeEventListener('keydown', f);
  });

  // Presupuesto del grupo con lo apuntado sumado
  const extra: Record<string, number> = {};
  for (const g of todos) extra[g.cat] = (extra[g.cat] ?? 0) + enEuros(g, yenPorEuro);
  const filas = v.dinero.presupuesto.map(p => ({ ...p, real: p.real + (extra[p.categoria] ?? 0) }));
  // Lo tuyo, por persona: cuánto llevas gastado para cada cual y los regalos
  const porPersona = new Map<string, number>();
  for (const g of todos) porPersona.set(g.para ?? 'Yo', (porPersona.get(g.para ?? 'Yo') ?? 0) + enEuros(g, yenPorEuro));
  const regalos = gastosConId().filter(g => g.regalo);
  const totalMio = [...porPersona.values()].reduce((a, b) => a + b, 0);
  const delDia = gastosConId().filter(g => g.dia === n).sort((a, b) => b.hora.localeCompare(a.hora));
  const plan = filas.reduce((s, p) => s + p.plan, 0), gastado = filas.reduce((s, p) => s + p.real, 0), margen = v.viaje.limite - plan;

  return (
    <Hoja id="hoja-gasto" titulo="gasto-titulo" onCerrada={onCerrada}>
      {() => (
        <>
          <p className="hoja__kicker">財布 · {n >= 1 && n <= v.dias.length ? `JP${num2(n)} · ` : ''}{editado ? `CORREGIR EL GASTO DE LAS ${editado.hora}` : 'APUNTAR UN GASTO'}</p>
          <h2 id="gasto-titulo" className="oculto">Apuntar un gasto</h2>
          <div className="saisen" aria-hidden="true"><Caja /></div>
          <div className="pantalla-yen">
            <output ref={salida} aria-live="polite">{moneda === 'JPY' ? `¥ ${miles(cifra)}` : euros(cifra / 100, 2)}</output>
            <small>≈ {moneda === 'JPY' ? euros(eur, 2) : `¥ ${miles(y)}`}</small>
          </div>
          <div className="moneda-cambio">
            <div className="filtros" role="group" aria-label="Moneda">
              <button type="button" aria-pressed={moneda === 'JPY'} onClick={() => cambiarMoneda('JPY')}>¥ yenes</button>
              <button type="button" aria-pressed={moneda === 'EUR'} onClick={() => cambiarMoneda('EUR')}>€ euros</button>
            </div>
            {editando
              ? <label className="cambio">1 € =<input type="number" inputMode="decimal" min="50" max="400" step="0.1" defaultValue={yenPorEuro} autoFocus
                  onBlur={e => { const x = +e.target.value; if (x >= 50 && x <= 400) ponerCambio(x === v.viaje.yenPorEuro ? null : x); setEditando(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); e.stopPropagation(); }} /> ¥</label>
              : <button type="button" className="cambio" onClick={() => setEditando(true)}>1 € = {miles(yenPorEuro)} ¥ · cambiar</button>}
          </div>
          <div className="cats" role="radiogroup" aria-label="De qué es el gasto">
            {CATEGORIAS.map(([c, t]) => <label key={c}><input type="radio" name="cat" value={c} checked={cat === c} onChange={() => setCat(c)} />{t}</label>)}
          </div>
          <div className="gasto-mas">
            <input className="concepto" type="text" value={concepto} maxLength={60} placeholder="Concepto (opcional): ramen, Suica…" aria-label="Concepto"
              onChange={e => setConcepto(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); e.stopPropagation(); } }} />
            <div className="filtros pago" role="group" aria-label="Para quién es el gasto">
              {[...PARA, 'Otro'].map(x => <button key={x} type="button" aria-pressed={para === x} onClick={() => elegirPara(x)}>{x === 'Yo' ? 'Para mí' : x}</button>)}
            </div>
            {para === 'Otro' && <input className="concepto" type="text" value={otro} maxLength={30} placeholder="¿Para quién? (un nombre)" aria-label="Para quién"
              onChange={e => { setOtro(e.target.value); try { localStorage.setItem(CLAVE_PARA, e.target.value); } catch { /* sin sitio */ } }} />}
            <label className="interruptor"><input type="checkbox" checked={esRegalo} onChange={e => setEsRegalo(e.target.checked)} /> 🎁 Es un regalo</label>
            {esRegalo && <input className="concepto" type="text" value={regalo} maxLength={60} placeholder="¿Qué regalo? (opcional)" aria-label="Qué regalo" onChange={e => setRegalo(e.target.value)} />}
          </div>
          <div className="teclado">{TECLAS.map(k => <button key={k} type="button" aria-label={k === '⌫' ? 'Borrar' : undefined} onClick={() => tecla(k)}>{k}</button>)}</div>
          {editado
            ? <div className="acciones">
                <button className="echar" type="button" disabled={!cifra} onClick={guardarEdicion}>Guardar el cambio</button>
                <button className="accion" type="button" onClick={borrar}>Borrar</button>
                <button className="accion" type="button" onClick={limpiar}>Cancelar</button>
              </div>
            : <button className="echar" type="button" disabled={!cifra} onClick={echar}>Echar a la caja</button>}
          <p className="total-dia">Hoy llevas <b ref={total}>¥ {miles(mostrado)}</b> · <span ref={totalEur}>{euros(mostrado / yenPorEuro, 2)}</span></p>
          {delDia.length > 0 && (
            <details className="plegable" open>
              <summary>Gastos del día · {delDia.length}</summary>
              <div><ul className="gastos">
                {delDia.map(g => (
                  <li key={g.id}>
                    <button type="button" aria-current={editado?.id === g.id} onClick={() => editar(g)}>
                      <time>{g.hora}</time>
                      <span className="que">{g.concepto || CATEGORIAS.find(c => c[0] === g.cat)?.[1] || g.cat}<small> · {g.regalo ? `🎁 ${g.regalo} · ` : ''}{g.para && g.para !== 'Yo' ? `para ${g.para}` : 'para mí'}</small></span>
                      <span className="importe">{g.yen != null ? `¥ ${miles(g.yen)}` : euros(g.eur ?? 0, 2)}</span>
                    </button>
                  </li>
                ))}
              </ul></div>
            </details>
          )}
          {totalMio > 0 && (
            <details className="plegable">
              <summary>Lo que llevas gastado · {euros(totalMio, 2)}</summary>
              <div>
                {[...porPersona].sort((a, b) => b[1] - a[1]).map(([quien, e]) => <p key={quien}><b>{quien === 'Yo' ? 'Para mí' : `Para ${quien}`}</b> · {euros(e, 2)}</p>)}
                {regalos.length > 0 && <><h4 className="doc-sub">Regalos</h4>{regalos.map(g => <p key={g.id}>🎁 {g.regalo}{g.para && g.para !== 'Yo' ? ` · para ${g.para}` : ''} · {g.yen != null ? `¥ ${miles(g.yen)}` : euros(g.eur ?? 0, 2)}</p>)}</>}
                <p className="texto" style={{ fontSize: '.8rem' }}>Los gastos son solo tuyos: Yago y Daniel no ven los suyos ni tú los de ellos.</p>
              </div>
            </details>
          )}
          <details className="plegable">
            <summary>Presupuesto del grupo (con lo que has apuntado tú)</summary>
            <div>
              <p><b>{euros(gastado)}</b> gastados de {euros(plan)} previstos. {margen < 0 ? <><b style={{ color: 'var(--shu)' }}>▲ {euros(-margen)} por encima</b> del límite de {euros(v.viaje.limite)}.</> : <>Quedan {euros(margen)} de margen.</>}</p>
              <table className="presupuesto">
                <thead><tr><th scope="col">Categoría</th><th scope="col" className="num">Gastado</th><th scope="col" className="num">Previsto</th><th scope="col"><span className="oculto">Proporción</span></th></tr></thead>
                <tbody>
                  {filas.map(p => {
                    const pasa = p.real > p.plan + 0.5;
                    return (
                      <tr key={p.categoria}>
                        <th scope="row">{p.categoria}</th><td className="num">{pasa ? '▲ ' : ''}{euros(p.real)}</td><td className="num">{euros(p.plan)}</td>
                        <td><div className={`pista ${pasa ? 'pasada' : ''}`} title={`${Math.round((p.real / p.plan) * 100)} %`}><i style={{ width: `${Math.min(100, (p.real / p.plan) * 100)}%` }} /></div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
          <p className="oculto" aria-live="polite" ref={anuncio} />
        </>
      )}
    </Hoja>
  );
}
